/**
 * Version and release information.
 *
 * @module
 */
import { APP_VERSION } from "../../branding";
import {
  canCheckForUpdate,
  getDesktopUpdateButtonTooltip,
  getDesktopUpdateInstallConfirmationMessage,
  isDesktopUpdateButtonDisabled,
  resolveDesktopUpdateButtonAction,
} from "../../components/desktopUpdate.logic";
import { isElectron } from "../../env";
import {
  setDesktopUpdateStateQueryData,
  useDesktopUpdateState,
} from "../../lib/desktopUpdateReactQuery";
import { ensureLocalApi } from "../../localApi";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { toastManager } from "../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { SettingsRow, SettingsSection } from "./settingsLayout";
import { DesktopUpdateChannel } from "@cmmd-forge/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

function AboutVersionTitle() {
  return (
    <span className="inline-flex items-center gap-2">
      <span>Version</span>
      <code className="text-[11px] font-medium text-muted-foreground">{APP_VERSION}</code>
    </span>
  );
}

const DESKTOP_UPDATE_CHANNEL_QUERY_KEY = ["desktop", "update-channel"] as const;

const UPDATE_CHANNEL_LABELS: Record<DesktopUpdateChannel, string> = {
  latest: "Stable",
  nightly: "Nightly",
};

// moved verbatim from the 2909-line ProviderSettings.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
function AboutVersionSection() {
  const queryClient = useQueryClient();
  const updateStateQuery = useDesktopUpdateState();

  const channelQuery = useQuery({
    queryKey: DESKTOP_UPDATE_CHANNEL_QUERY_KEY,
    queryFn: async (): Promise<DesktopUpdateChannel> => {
      const bridge = window.desktopBridge;
      if (!bridge?.getUpdateChannel) return "latest";
      return bridge.getUpdateChannel();
    },
    enabled: Boolean(window.desktopBridge?.getUpdateChannel),
    staleTime: Infinity,
  });

  const currentChannel = channelQuery.data ?? "latest";

  const handleChannelChange = useCallback(
    (value: DesktopUpdateChannel | null) => {
      if (!value) return;
      const bridge = window.desktopBridge;
      if (!bridge?.setUpdateChannel) return;
      void bridge.setUpdateChannel(value).then(() => {
        void queryClient.invalidateQueries({ queryKey: DESKTOP_UPDATE_CHANNEL_QUERY_KEY });
      });
    },
    [queryClient],
  );

  const updateState = updateStateQuery.data ?? null;

  const handleButtonClick = useCallback(() => {
    const bridge = window.desktopBridge;
    if (!bridge) return;

    const action = updateState ? resolveDesktopUpdateButtonAction(updateState) : "none";

    if (action === "download") {
      void bridge
        .downloadUpdate()
        .then((result) => {
          setDesktopUpdateStateQueryData(queryClient, result.state);
        })
        .catch((error: unknown) => {
          toastManager.add({
            type: "error",
            title: "Could not download update",
            description: error instanceof Error ? error.message : "Download failed.",
          });
        });
      return;
    }

    if (action === "install") {
      void ensureLocalApi()
        .dialogs.confirm(
          getDesktopUpdateInstallConfirmationMessage(
            updateState ?? { availableVersion: null, downloadedVersion: null },
          ),
        )
        .then((confirmed) => {
          if (!confirmed) return null;
          return bridge.installUpdate().then((result) => {
            setDesktopUpdateStateQueryData(queryClient, result.state);
          });
        })
        .catch((error: unknown) => {
          toastManager.add({
            type: "error",
            title: "Could not install update",
            description: error instanceof Error ? error.message : "Install failed.",
          });
        });
      return;
    }

    if (typeof bridge.checkForUpdate !== "function") return;
    void bridge
      .checkForUpdate()
      .then((result) => {
        setDesktopUpdateStateQueryData(queryClient, result.state);
        if (!result.checked) {
          toastManager.add({
            type: "error",
            title: "Could not check for updates",
            description:
              result.state.message ?? "Automatic updates are not available in this build.",
          });
        }
      })
      .catch((error: unknown) => {
        toastManager.add({
          type: "error",
          title: "Could not check for updates",
          description: error instanceof Error ? error.message : "Update check failed.",
        });
      });
  }, [queryClient, updateState]);

  const action = updateState ? resolveDesktopUpdateButtonAction(updateState) : "none";
  const buttonTooltip = updateState ? getDesktopUpdateButtonTooltip(updateState) : null;
  const buttonDisabled =
    action === "none"
      ? !canCheckForUpdate(updateState)
      : isDesktopUpdateButtonDisabled(updateState);

  const actionLabel: Record<string, string> = { download: "Download", install: "Install" };
  const statusLabel: Record<string, string> = {
    checking: "Checking…",
    downloading: "Downloading…",
    "up-to-date": "Up to Date",
  };
  const buttonLabel =
    actionLabel[action] ?? statusLabel[updateState?.status ?? ""] ?? "Check for Updates";
  const description =
    action === "download" || action === "install"
      ? "Update available."
      : "Current version of the application.";

  const showChannelPicker = Boolean(window.desktopBridge?.getUpdateChannel);

  return (
    <>
      <SettingsRow
        title={<AboutVersionTitle />}
        description={description}
        control={
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="xs"
                  variant={action === "install" ? "default" : "outline"}
                  disabled={buttonDisabled}
                  onClick={handleButtonClick}
                >
                  {buttonLabel}
                </Button>
              }
            />
            {buttonTooltip ? <TooltipPopup>{buttonTooltip}</TooltipPopup> : null}
          </Tooltip>
        }
      />
      {showChannelPicker && (
        <SettingsRow
          title="Update channel"
          description="Switch between Stable (released builds) and Nightly (hourly builds). Triggers an immediate update check."
          control={
            <Select value={currentChannel} onValueChange={handleChannelChange}>
              <SelectTrigger className="w-full sm:w-40" aria-label="Update channel">
                <SelectValue>{UPDATE_CHANNEL_LABELS[currentChannel]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {(Object.entries(UPDATE_CHANNEL_LABELS) as [DesktopUpdateChannel, string][]).map(
                  ([value, label]) => (
                    <SelectItem key={value} hideIndicator value={value}>
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectPopup>
            </Select>
          }
        />
      )}
    </>
  );
}

export function AboutSection() {
  return (
    <SettingsSection id="about" title="About">
      {isElectron ? (
        <AboutVersionSection />
      ) : (
        <SettingsRow
          title={<AboutVersionTitle />}
          description="Current version of the application."
        />
      )}
    </SettingsSection>
  );
}
